import { useCallback, useMemo } from 'react';
import { Button } from '@dsg-tech/homefield-react';
import { getUserToken } from 'utility/auth-utils';
import { getIsValidHost } from 'utility/common-utils';
import { makeApiRequest } from '../../utility/utils';

type SelectedSkuType =
  | {
      partNumber: string;
    }
  | null
  | undefined;

type CallbackArgumentType = {
  state: 'SUCCESS' | 'FAILURE';
  sku?: string;
};

type SaveSelectionPropsType = {
  onClose: () => void;
  ecode: string;
  selectedSku: SelectedSkuType;
  skuDefaultValue: string;
  callback?: (param: CallbackArgumentType) => void;
  isProductOOS: boolean;
};

const SaveSelection = ({
  onClose,
  ecode,
  selectedSku,
  skuDefaultValue,
  callback,
  isProductOOS,
}: SaveSelectionPropsType) => {
  const host = window.location ? window.location.host : '';

  /**
   * Returns the URL for the favorites API endpoint.
   * @returns The URL for the favorites API endpoint.
   */
  const getFavoritesUrl = useCallback(() => {
    let returnUrl;

    if (getIsValidHost() || host.includes('dksxchange')) {
      returnUrl = `https://${window.location.host}/v1/mylocker`;
    } else {
      returnUrl = `/favorites-api/v1/mylocker`;
    }

    return returnUrl;
  }, [host]);

  const parsedSku = useMemo(() => {
    if (!selectedSku) return null;

    return selectedSku.partNumber;
  }, [selectedSku]);

  const saveSelection = useCallback(async () => {
    await makeApiRequest({
      endpoint: `${getFavoritesUrl()}/products/update`,
      method: 'PUT',
      data: {
        sku: parsedSku,
        id: ecode,
      },
      getUserToken,
      onClose,
      callback,
      eventData: { EditedProductID: ecode, EditedProductSKU: skuDefaultValue },
    });
  }, [parsedSku, skuDefaultValue, ecode, onClose, callback]);

  const shouldBeDisabled = useMemo(() => {
    // if the product is out of stock, disable the button
    if (isProductOOS) return true;
    // if the sku is not valid (invalid combination or not selected anything), disable the button
    if (!parsedSku) return true;
    // if the sku is the same as the default sku, disable the button
    return parsedSku === skuDefaultValue;
  }, [parsedSku, skuDefaultValue, isProductOOS]);

  return (
    <div
      className="quickview-button-save-selection-container hmf-position-absolute hmf-p-s hmf-w-100 hmf-elevated-up-shadow"
      data-testid="quickViewModal-save-selection-buttons">
      <Button
        variant="primary"
        label="Save selections"
        text="Save selections"
        onClick={saveSelection}
        disabled={shouldBeDisabled}
        className="hmf-w-100 "
      />
    </div>
  );
};

export default SaveSelection;
